/**
 * INNFLOW DESIGN SYSTEM V2.0 - Icon Component
 * 
 * Wrapper para ícones lucide-react com tamanhos e cores padronizados
 * Conforme especificação INNFLOW System Architecture
 * 
 * @version 2.0.0
 * @author INNFLOW Design Team
 */

import React from 'react';
import * as LucideIcons from 'lucide-react';
import { cn } from '@/lib/utils';
import './Icon.css';

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type IconColor = 'primary' | 'secondary' | 'accent' | 'muted' | 'foreground' | 'success' | 'warning' | 'error';

export interface IconProps {
  name: keyof typeof LucideIcons;
  size?: IconSize;
  color?: IconColor;
  className?: string;
  'aria-label'?: string;
  'aria-hidden'?: boolean;
  animated?: 'rotate' | 'pulse' | 'spin';
}

/**
 * Icon - Wrapper para ícones lucide-react
 * 
 * Integra com lucide-react para ícones consistentes
 * Suporta tamanhos, cores e animações do design system
 */
export const Icon: React.FC<IconProps> = ({
  name,
  size = 'md',
  color = 'foreground',
  className,
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden = true,
  animated,
}) => {
  const LucideIcon = LucideIcons[name] as React.ComponentType<{
    size?: number | string;
    className?: string;
    'aria-label'?: string;
    'aria-hidden'?: boolean;
  }>;

  if (!LucideIcon) {
    console.warn(`Icon "${name}" not found in lucide-react`);
    return null;
  }

  return (
    <LucideIcon
      className={cn(
        'innflow-icon',
        `innflow-icon--${size}`,
        `innflow-icon--${color}`,
        {
          [`innflow-icon--animated-${animated}`]: animated,
        },
        className
      )}
      aria-label={ariaLabel}
      aria-hidden={ariaHidden}
    />
  );
};

export default Icon;
